/**
 * Create the site's 24-Hour Line (docs/plans/oncall-line-plan.md 7.5 POST
 * /line, 7.1, 11 step 5; WP-11). Shown by the Settings tab when the site has
 * no line yet: the runbook creates it here from the number bought for the
 * site, so no SQL is needed.
 *
 * One active line per site (409 ONCALL_LINE_EXISTS: someone else just
 * created it; the tab reloads) and a number belongs to one line (409
 * ONCALL_NUMBER_IN_USE). The new line starts with the hunt defaults of 2.3
 * and the creating admin as its alert recipient. Creating the line does not
 * route any call to it: the number's Voice URL in the Twilio console does,
 * and the runbook points it here last.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Loader2, PhoneCall } from 'lucide-react';
import { fieldCls, primaryBtnCls } from '../../../ui/styles';
import { apiErrorCode, apiErrorMessage, createLine } from '../../api/oncallApi';
import type { OncallLine } from '../../api/oncallApi';
import { toUsE164, usNumberProblem } from '../team/phone';
import { MAX_LINE_NAME_LENGTH } from './huntMath';
import { squash } from './settingsForm';

export interface CreateLineFormProps {
  siteName: string | null | undefined;
  onCreated: (line: OncallLine) => void;
  /** Someone else created the site's line meanwhile: reload it. */
  onExists: () => void;
}

export default function CreateLineForm({ siteName, onCreated, onExists }: CreateLineFormProps) {
  const [number, setNumber] = useState('');
  const [name, setName] = useState(siteName ? `${siteName} 24-Hour Line` : '24-Hour Line');
  const [errors, setErrors] = useState<{ number?: string; name?: string; form?: string }>({});
  const [saving, setSaving] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    const next: typeof errors = {};
    const numberProblem = usNumberProblem(number);
    if (numberProblem) next.number = numberProblem;
    const cleanName = squash(name);
    if (!cleanName) next.name = 'Give the line a name.';
    else if (cleanName.length > MAX_LINE_NAME_LENGTH) next.name = `At most ${MAX_LINE_NAME_LENGTH} characters.`;
    setErrors(next);
    if (next.number || next.name) return;

    setSaving(true);
    try {
      const line = await createLine({ twilio_number_e164: toUsE164(number) as string, name: cleanName });
      onCreated(line);
    } catch (err) {
      if (apiErrorCode(err) === 'ONCALL_LINE_EXISTS') {
        onExists();
        return;
      }
      setErrors({ form: apiErrorMessage(err, 'The line could not be created.') });
      setSaving(false);
    }
  };

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800" aria-labelledby="oncall-create-line-title" data-testid="oncall-create-line">
      <h2 id="oncall-create-line-title" className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        <PhoneCall className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
        Create this site&apos;s line
      </h2>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
        {siteName ? `${siteName} has` : 'This site has'} no 24-Hour Line yet. Enter the local number bought for this
        site in Twilio (the one printed on its consent forms). Calls reach SiteOps only once the number&apos;s Voice URL
        points here, which the launch runbook does last.
      </p>
      <form onSubmit={submit} noValidate className="mt-4 space-y-4">
        {errors.form && (
          <p role="alert" data-testid="oncall-create-line-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {errors.form}
          </p>
        )}
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="oncall-line-number" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
              Twilio number
            </label>
            <input
              id="oncall-line-number"
              type="tel"
              inputMode="tel"
              autoComplete="off"
              placeholder="(555) 234-5678"
              value={number}
              onChange={(e) => {
                setNumber(e.target.value);
                if (errors.number) setErrors((prev) => ({ ...prev, number: undefined }));
              }}
              aria-invalid={errors.number ? true : undefined}
              aria-describedby={errors.number ? 'oncall-line-number-error' : undefined}
              className={`${fieldCls} w-full`}
            />
            {errors.number && (
              <p id="oncall-line-number-error" className="mt-1 text-xs text-red-600 dark:text-red-400">
                {errors.number}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="oncall-line-name" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
              Line name
            </label>
            <input
              id="oncall-line-name"
              type="text"
              autoComplete="off"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
              }}
              aria-invalid={errors.name ? true : undefined}
              aria-describedby={errors.name ? 'oncall-line-name-error' : undefined}
              className={`${fieldCls} w-full`}
            />
            {errors.name && (
              <p id="oncall-line-name-error" className="mt-1 text-xs text-red-600 dark:text-red-400">
                {errors.name}
              </p>
            )}
          </div>
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={saving} data-testid="oncall-create-line-submit" className={primaryBtnCls}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Create line
          </button>
        </div>
      </form>
    </section>
  );
}
